import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography, Button, Tag, Steps, Select, Input, Space, Spin, Modal, Tooltip, Card, message, Empty, Collapse, Divider
} from "antd";
import {
  MailOutlined, PauseCircleOutlined, PlayCircleOutlined,
  StopOutlined, PlusOutlined, RocketOutlined, ExclamationCircleOutlined
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock, faCircleCheck, faEye, faReply, faCircleXmark, faStop,
  faArrowRight, faPaperPlane, faTriangleExclamation, faChevronRight, faChevronDown
} from "@fortawesome/free-solid-svg-icons";
import {
  createEmailSequenceThunk,
  updateEmailSequenceStatusThunk,
  getEmailSequencesByLeadThunk,
  fetchAllEmailTemplates,
} from "../../../Slices/salesSlice";
import { FetchReplysThunk } from "../../../Slices/Thunk/notification";
import { setRepliesForEmail } from "../../../Slices/emailRepliesSlice";
import { setLoader } from "../../../Slices/loaderSlice";
import axios from "axios";
import { SERVER_URL } from "../../../databucket";
import socket from "./socket";
import EmailThread from "./EmailThread";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const EMAIL_TYPES = [
  { value: "cold-outreach", label: "Cold Outreach" },
  { value: "follow-up-no-reply", label: "Follow-Up (No Reply)" },
  { value: "post-call-follow-up", label: "Post-Call Follow-Up" },
  { value: "re-engagement", label: "Re-Engagement" },
  { value: "ops-intro", label: "Ops Team Intro" },
  { value: "proposal-follow-up", label: "Proposal Follow-Up" },
];

const SEQ_STATUS_COLORS = {
  active: "processing", paused: "warning", completed: "success", cancelled: "default",
};

const DELIVERY_STATUS_CONFIG = {
  pending:      { color: "#f59e0b", bg: "#fffbeb", label: "Pending",           icon: faClock,        border: "#fcd34d" },
  delivered:    { color: "#10b981", bg: "#ecfdf5", label: "Delivered",         icon: faCircleCheck,  border: "#6ee7b7" },
  opened:       { color: "#3b82f6", bg: "#eff6ff", label: "Opened",            icon: faEye,          border: "#93c5fd" },
  replied:      { color: "#8b5cf6", bg: "#f5f3ff", label: "Response Received", icon: faReply,        border: "#c4b5fd" },
  bounced:      { color: "#ef4444", bg: "#fef2f2", label: "Hard Bounce",       icon: faCircleXmark,  border: "#fca5a5" },
  complained:   { color: "#dc2626", bg: "#fef2f2", label: "Spam Complaint",    icon: faStop,         border: "#f87171" },
};

const DeliveryBadge = ({ status }) => {
  const cfg = DELIVERY_STATUS_CONFIG[status] || DELIVERY_STATUS_CONFIG.pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 11,
      fontWeight: 700, color: cfg.color, background: cfg.bg,
      border: `1.5px solid ${cfg.border}`, letterSpacing: "0.2px",
    }}>
      <FontAwesomeIcon icon={cfg.icon} style={{ fontSize: 10 }} />
      {cfg.label}
    </span>
  );
};

const DEFAULT_STEPS = [
  { stepNumber: 1, delayDays: 0, emailType: "cold-outreach" },
  { stepNumber: 2, delayDays: 3, emailType: "follow-up-no-reply" },
  { stepNumber: 3, delayDays: 5, emailType: "re-engagement" },
];

const EmailSequencePanel = ({ leadId, leadName, contacts, repName, repEmail }) => {
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const templates = useSelector((state) => state?.sales?.emailTemplates || []);

  // Thread expansion state
  const [expandedSteps, setExpandedSteps] = useState({});     // { "seqId-stepIdx": true }
  const [stepReplies, setStepReplies] = useState({});          // { "messageId": [replies] }
  const [loadingReplies, setLoadingReplies] = useState({});    // { "messageId": true }
  const [deliveryStatuses, setDeliveryStatuses] = useState({}); // { trackId: "delivered" }
  const repliesFromRedux = useSelector((state) => state?.emailReplies?.repliesByEmailId || {});

  // Create form state
  const [seqName, setSeqName] = useState("Default Sequence");
  const [contactEmail, setContactEmail] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [steps, setSteps] = useState([...DEFAULT_STEPS]);

  // Sales email accounts the user has access to
  const salesEmails = useMemo(() => {
    return (loginUser?.salesEmailSettings || [])
      .filter((s) => s.status === true || s.status === "true")
      .map((s) => s.email)
      .filter(Boolean);
  }, [loginUser]);

  useEffect(() => {
    if (leadId) loadSequences();
  }, [leadId]);

  // Listen for real-time sequence updates via socket
  useEffect(() => {
    const handleStepSent = (data) => {
      if (data?.leadId === leadId) loadSequences();
    };
    const handlePaused = (data) => {
      if (data?.leadId === leadId) loadSequences();
    };

    socket.on('sequence:stepSent', handleStepSent);
    socket.on('sequence:paused', handlePaused);
    return () => {
      socket.off('sequence:stepSent', handleStepSent);
      socket.off('sequence:paused', handlePaused);
    };
  }, [leadId]);

  // Pre-select first contact email and from email
  useEffect(() => {
    if (contacts?.length > 0) {
      const firstEmail = contacts.find((c) => c.email)?.email;
      if (firstEmail) setContactEmail(firstEmail);
    }
  }, [contacts]);

  useEffect(() => {
    if (salesEmails.length > 0 && !fromEmail) setFromEmail(salesEmails[0]);
  }, [salesEmails]);

  // Load templates when modal opens
  useEffect(() => {
    if (showCreate && loginUser?.id) {
      dispatch(fetchAllEmailTemplates(loginUser.id));
    }
  }, [showCreate, loginUser?.id]);

  const loadSequences = async () => {
    setLoading(true);
    try {
      const result = await dispatch(getEmailSequencesByLeadThunk({ leadId })).unwrap();
      if (result?.sequences) {
        setSequences(result.sequences);
        // Sync delivery statuses for all sent steps with trackIds
        const trackIds = result.sequences
          .flatMap((s) => (s.steps || []).filter((st) => st.trackId).map((st) => st.trackId));
        if (trackIds.length > 0) {
          try {
            const { data: statusRes } = await axios.post(`${SERVER_URL}/api/ses/status-sync`, { trackIds });
            if (statusRes?.statuses) setDeliveryStatuses(statusRes.statuses);
          } catch { /* non-fatal */ }
        }
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  // ── Thread expansion helpers ──
  const toggleThread = async (stepKey, messageId, emailFrom, replyMessageIds) => {
    if (expandedSteps[stepKey]) {
      setExpandedSteps((prev) => ({ ...prev, [stepKey]: false }));
      return;
    }
    if (messageId && emailFrom) {
      setLoadingReplies((prev) => ({ ...prev, [messageId]: true }));
      try {
        const res = await dispatch(FetchReplysThunk({ email: emailFrom, messageId, replyMessageIds: replyMessageIds || [] })).unwrap();
        const fetched = res?.replies || [];
        if (fetched.length > 0) {
          dispatch(setRepliesForEmail({ emailID: messageId, replies: fetched }));
          setStepReplies((prev) => ({ ...prev, [messageId]: fetched }));
        } else {
          setStepReplies((prev) => ({ ...prev, [messageId]: [] }));
        }
      } catch { setStepReplies((prev) => ({ ...prev, [messageId]: [] })); }
      setLoadingReplies((prev) => ({ ...prev, [messageId]: false }));
    }
    setExpandedSteps((prev) => ({ ...prev, [stepKey]: true }));
  };

  const getRepliesForStep = (messageId) => {
    return stepReplies[messageId] || repliesFromRedux[messageId] || [];
  };

  const handleCreate = async () => {
    if (!contactEmail) { message.warning("Please select a contact email"); return; }
    if (!fromEmail) { message.warning("Please select a 'From' email address"); return; }
    setCreating(true);
    try {
      const result = await dispatch(
        createEmailSequenceThunk({
          leadId, leadName, sequenceName: seqName, contactEmail, fromEmail,
          repName, repEmail, createdBy: loginUser?.id || repName, steps,
        })
      ).unwrap();
      if (result?.status === 200) {
        message.success("Email sequence started!");
        setShowCreate(false);
        setSteps([...DEFAULT_STEPS]);
        loadSequences();
      } else {
        message.error(result?.message || "Failed to create sequence");
      }
    } catch (err) {
      message.error(err || "Failed to create sequence");
    }
    setCreating(false);
  };

  const handleStatusChange = async (sequenceId, newStatus) => {
    try {
      const result = await dispatch(
        updateEmailSequenceStatusThunk({ sequenceId, status: newStatus })
      ).unwrap();
      if (result?.status === 200) {
        message.success(`Sequence ${newStatus}`);
        loadSequences();
      } else { message.error(result?.message); }
    } catch (err) { message.error(err || "Failed to update"); }
  };

  const addStep = () => {
    setSteps((prev) => [...prev, { stepNumber: prev.length + 1, delayDays: 3, emailType: "follow-up-no-reply" }]);
  };

  const updateStep = (index, field, value) => {
    setSteps((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // If template selected, auto-fill subject/body
      if (field === "templateId") {
        const tpl = templates.find((t) => t.id === value || t._id === value);
        if (tpl) {
          updated[index].templateName = tpl.name;
          updated[index].subject = tpl.subject || "";
          updated[index].body = tpl.body || "";
        }
      }
      return updated;
    });
  };

  const removeStep = (index) => {
    setSteps((prev) => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, stepNumber: i + 1 })));
  };

  const contactEmails = (contacts || []).filter((c) => c.email).map((c) => ({
    value: c.email,
    label: `${c.contactPerson || ""} <${c.email}>`,
  }));

  const templateOptions = templates.map((t) => ({
    value: t.id || t._id,
    label: `${t.name}${t.category ? ` (${t.category})` : ""}`,
  }));

  const hasActiveSequence = sequences.some((s) => s.status === "active");

  // ── Render a sent step as EmailTrack-style card ──
  const renderSentStepCard = (step, seq, stepIdx) => {
    const sentDate = step.sentAt ? dayjs(parseInt(step.sentAt) || step.sentAt) : null;
    const deliveryStatus = deliveryStatuses[step.trackId] || (step.trackId ? "pending" : "sent");
    const stepKey = `${seq.id}-${stepIdx}`;
    const isExpanded = expandedSteps[stepKey];
    const isLoadingThread = loadingReplies[step.messageId];
    const replies = getRepliesForStep(step.messageId);

    return (
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
        marginTop: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", overflow: "hidden",
      }}>
        {/* Email Card Header */}
        <div style={{ padding: "12px 16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flex: 1 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%", background: "#6366f1",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontWeight: 700, fontSize: 14, flexShrink: 0,
              }}>
                {(seq.fromEmail || seq.repEmail || "?")[0]?.toUpperCase()}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                  <span style={{ color: "#3b82f6" }}>{seq.fromEmail || seq.repEmail}</span>
                  <FontAwesomeIcon icon={faArrowRight} style={{ margin: "0 6px", fontSize: 10, color: "#94a3b8" }} />
                  <span>{seq.contactEmail}</span>
                </div>
                <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
                  <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 4, fontSize: 10, color: "#94a3b8" }} />
                  {step.subject || step.emailType || "No subject"}
                </div>
                {step.templateName && (
                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                    Template: {step.templateName}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
              {sentDate && (
                <Tooltip title={sentDate.format("DD MMM YYYY HH:mm:ss")}>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>{sentDate.fromNow()}</span>
                </Tooltip>
              )}
              <DeliveryBadge status={deliveryStatus} />
            </div>
          </div>
        </div>

        {/* Thread toggle row */}
        {step.messageId && (
          <div
            onClick={() => toggleThread(stepKey, step.messageId, seq.fromEmail || seq.repEmail)}
            style={{
              padding: "8px 16px", borderTop: "1px solid #f1f5f9",
              background: isExpanded ? "#f8faff" : "#fafafa",
              display: "flex", alignItems: "center", gap: 8,
              cursor: "pointer", userSelect: "none", transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f4ff")}
            onMouseLeave={(e) => (e.currentTarget.style.background = isExpanded ? "#f8faff" : "#fafafa")}
          >
            <div style={{
              width: 20, height: 20, borderRadius: 6,
              background: isExpanded ? "#6366f1" : "#e2e8f0",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.15s",
            }}>
              <FontAwesomeIcon
                icon={isLoadingThread ? faClock : isExpanded ? faChevronDown : faChevronRight}
                style={{ fontSize: 9, color: isExpanded ? "#fff" : "#64748b" }}
              />
            </div>
            <span style={{ fontSize: 12, color: isExpanded ? "#6366f1" : "#64748b", fontWeight: 600 }}>
              {isLoadingThread ? "Fetching replies..."
                : isExpanded
                  ? (() => { const rc = replies.filter((r) => r.provider !== "ses-sent").length; return `Hide thread${rc > 0 ? ` (${rc} repl${rc === 1 ? "y" : "ies"})` : ""}`; })()
                  : "View thread & replies"}
            </span>
          </div>
        )}

        {/* Expanded thread */}
        {isExpanded && (
          <div style={{ borderTop: "1px solid #e2e8f0", background: "#f8faff", padding: "0 0 8px 0" }}>
            {replies.length > 0 ? (
              <EmailThread
                replies={replies}
                userEmail={seq.fromEmail || seq.repEmail}
                originalEmailId={step.messageId}
              />
            ) : (
              <div style={{ padding: "20px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                No replies found yet.
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Render a pending step ──
  const renderPendingStep = (step, seq, idx) => {
    const isNext = idx === seq.currentStep && seq.status === "active";
    return (
      <div style={{
        background: isNext ? "#eff6ff" : "#f8fafc",
        border: `1px ${isNext ? "solid" : "dashed"} ${isNext ? "#93c5fd" : "#e2e8f0"}`,
        borderRadius: 10, padding: "10px 16px", marginTop: 8, opacity: isNext ? 1 : 0.7,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <span style={{ fontSize: 12, fontWeight: 600, color: isNext ? "#1e40af" : "#64748b" }}>
              {EMAIL_TYPES.find((t) => t.value === step.emailType)?.label || step.emailType}
            </span>
            {step.templateName && (
              <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 8 }}>({step.templateName})</span>
            )}
          </div>
          <Tag color={isNext ? "blue" : "default"} style={{ borderRadius: 10, fontSize: 10 }}>
            {isNext ? "Next — runs soon" : `Scheduled +${step.delayDays}d`}
          </Tag>
        </div>
        {step.subject && (
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
            Subject: {step.subject}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      marginTop: "15px", backgroundColor: "#fff", padding: "12px 16px",
      boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)", borderRadius: "8px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          <MailOutlined style={{ marginRight: 8 }} />
          Email Sequences
        </Typography.Title>
        {!hasActiveSequence && (
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setShowCreate(true)}>
            New Sequence
          </Button>
        )}
      </div>

      {loading ? (
        <Spin size="small" />
      ) : sequences.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No email sequences yet" style={{ margin: "8px 0" }} />
      ) : (
        sequences.map((seq) => (
          <Card
            key={seq.id}
            size="small"
            style={{ marginBottom: 10, borderRadius: 10 }}
            title={
              <Space>
                <span style={{ fontWeight: 600 }}>{seq.sequenceName}</span>
                <Tag color={SEQ_STATUS_COLORS[seq.status]}>{seq.status?.toUpperCase()}</Tag>
                {seq.pauseReason === "reply_received" && (
                  <Tag color="purple" icon={<FontAwesomeIcon icon={faReply} style={{ marginRight: 4 }} />}>
                    Reply Received
                  </Tag>
                )}
                {seq.pauseReason === "bounced" && (
                  <Tag color="red" icon={<FontAwesomeIcon icon={faCircleXmark} style={{ marginRight: 4 }} />}>
                    Email Bounced
                  </Tag>
                )}
              </Space>
            }
            extra={
              <Space size="small">
                {seq.status === "active" && (
                  <Tooltip title="Pause">
                    <Button size="small" icon={<PauseCircleOutlined />} onClick={() => handleStatusChange(seq.id, "paused")} />
                  </Tooltip>
                )}
                {seq.status === "paused" && (
                  <Tooltip title="Resume">
                    <Button size="small" type="primary" ghost icon={<PlayCircleOutlined />} onClick={() => handleStatusChange(seq.id, "active")} />
                  </Tooltip>
                )}
                {(seq.status === "active" || seq.status === "paused") && (
                  <Tooltip title="Cancel">
                    <Button size="small" danger icon={<StopOutlined />} onClick={() => handleStatusChange(seq.id, "cancelled")} />
                  </Tooltip>
                )}
              </Space>
            }
          >
            {/* Reply-pause warning banner */}
            {seq.pauseReason === "reply_received" && (
              <div style={{
                background: "#faf5ff", border: "1px solid #c4b5fd", borderRadius: 8,
                padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#6d28d9",
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <span><strong>Sequence paused</strong> — the contact replied. Review their response and resume or cancel the sequence.</span>
              </div>
            )}
            {/* Bounce-cancelled banner */}
            {seq.pauseReason === "bounced" && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8,
                padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#dc2626",
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <FontAwesomeIcon icon={faCircleXmark} />
                <span><strong>Sequence cancelled</strong> — the email bounced or was rejected. This email address may be invalid.</span>
              </div>
            )}

            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
              To: <strong>{seq.contactEmail}</strong>
              {seq.fromEmail && <span> | From: <strong>{seq.fromEmail}</strong></span>}
              {seq.nextRunDate && seq.status === "active" && (
                <span> | Next: {dayjs(parseInt(seq.nextRunDate) || seq.nextRunDate).format("DD MMM, hh:mm A")}</span>
              )}
            </div>

            {/* Steps — EmailTrack-style for sent, card for pending */}
            {seq.steps?.map((step, idx) => (
              <div key={idx}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: idx > 0 ? 12 : 4 }}>
                  <Tag color={step.status === "sent" ? "green" : step.status === "skipped" ? "orange" : "blue"} style={{ minWidth: 60, textAlign: "center", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                    Step {step.stepNumber}
                  </Tag>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>
                    {EMAIL_TYPES.find((t) => t.value === step.emailType)?.label || step.emailType}
                    {step.delayDays > 0 && ` (+${step.delayDays}d)`}
                  </span>
                </div>
                {step.status === "sent" ? renderSentStepCard(step, seq, idx) : step.status === "skipped" ? (
                  <div style={{ background: "#fff7ed", border: "1px dashed #fdba74", borderRadius: 10, padding: "8px 16px", marginTop: 8, fontSize: 12, color: "#c2410c" }}>
                    Skipped — reply was received before this step
                  </div>
                ) : renderPendingStep(step, seq, idx)}
              </div>
            ))}
          </Card>
        ))
      )}

      {/* Create Sequence Modal */}
      <Modal
        title={<Space><RocketOutlined /><span>Start Email Sequence for {leadName}</span></Space>}
        open={showCreate}
        onCancel={() => setShowCreate(false)}
        onOk={handleCreate}
        confirmLoading={creating}
        okText="Start Sequence"
        width={650}
      >
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontWeight: 600, fontSize: 12, color: "#374151" }}>Sequence Name</label>
          <Input size="small" value={seqName} onChange={(e) => setSeqName(e.target.value)} style={{ marginTop: 4 }} />
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontWeight: 600, fontSize: 12, color: "#374151" }}>From Email</label>
            <Select
              size="small"
              style={{ width: "100%", marginTop: 4 }}
              value={fromEmail}
              onChange={setFromEmail}
              options={salesEmails.map((e) => ({ value: e, label: e }))}
              placeholder="Select sender email"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontWeight: 600, fontSize: 12, color: "#374151" }}>Send To</label>
            <Select
              size="small"
              style={{ width: "100%", marginTop: 4 }}
              value={contactEmail}
              onChange={setContactEmail}
              options={contactEmails}
              placeholder="Select contact email"
            />
          </div>
        </div>

        <Divider style={{ margin: "12px 0 8px" }} />
        <label style={{ fontWeight: 600, fontSize: 12, color: "#374151" }}>Steps</label>
        <div style={{ marginTop: 8 }}>
          {steps.map((step, idx) => (
            <div key={idx} style={{
              display: "flex", gap: 8, alignItems: "center", marginBottom: 8,
              padding: "8px 10px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0",
            }}>
              <Tag color="blue" style={{ minWidth: 28, textAlign: "center", borderRadius: 10, fontWeight: 700 }}>{idx + 1}</Tag>
              <Select
                size="small"
                style={{ width: 160 }}
                value={step.emailType}
                onChange={(val) => updateStep(idx, "emailType", val)}
                options={EMAIL_TYPES}
              />
              {templateOptions.length > 0 && (
                <Select
                  size="small"
                  style={{ flex: 1 }}
                  value={step.templateId}
                  onChange={(val) => updateStep(idx, "templateId", val)}
                  options={templateOptions}
                  placeholder="Pick template"
                  allowClear
                />
              )}
              <span style={{ fontSize: 11, whiteSpace: "nowrap", color: "#64748b" }}>after</span>
              <Input
                size="small" type="number" min={0} style={{ width: 50 }}
                value={step.delayDays}
                onChange={(e) => updateStep(idx, "delayDays", parseInt(e.target.value) || 0)}
              />
              <span style={{ fontSize: 11, whiteSpace: "nowrap", color: "#64748b" }}>days</span>
              {steps.length > 1 && (
                <Button size="small" danger type="text" onClick={() => removeStep(idx)}>X</Button>
              )}
            </div>
          ))}
          {steps.map((step, idx) => step.subject ? (
            <div key={`preview-${idx}`} style={{ fontSize: 11, color: "#94a3b8", marginTop: -4, marginBottom: 8, paddingLeft: 38 }}>
              Subject: {step.subject}
            </div>
          ) : null)}
        </div>
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addStep} block style={{ marginTop: 4 }}>
          Add Step
        </Button>
        <div style={{ marginTop: 12, fontSize: 11, color: "#94a3b8" }}>
          Steps without a template will have their email auto-generated by AI based on the lead's context.
          The cron runs every hour and processes due steps automatically.
        </div>
      </Modal>
    </div>
  );
};

export default EmailSequencePanel;
